import { useState, useEffect } from "react";
import { CheckCircle2, AlertTriangle, Loader2, ShieldCheck, RefreshCw, Download, ExternalLink } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";

/**
 * Phase 2L-A54 — Admin tool to reconcile stuck bookings against Stripe.
 *
 * SAFETY: This component NEVER calls a write endpoint without first
 * receiving an explicit dry-run result with `confidence === "high"` AND
 * the user clicking the "Apply" button on a confirmation step.
 */
export function ReconcileFromStripeDialog({ open, onClose, onApplied }) {
  // ── Tab: Single booking ───────────────────────────────────────────
  const [ref, setRef] = useState("");
  const [dryRun, setDryRun] = useState(null);   // null | response object
  const [applying, setApplying] = useState(false);
  const [checking, setChecking] = useState(false);

  // ── Tab: 30-day scan ─────────────────────────────────────────────
  const [scanDays, setScanDays] = useState(30);
  const [scanReport, setScanReport] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [selectedScanRefs, setSelectedScanRefs] = useState(() => new Set());
  const [scanApplyBusy, setScanApplyBusy] = useState(false);

  // Reset everything when the dialog closes so a stale dry-run from
  // a previous booking can't be applied to the next one by accident.
  useEffect(() => {
    if (!open) {
      setRef("");
      setDryRun(null);
      setChecking(false);
      setApplying(false);
      setScanReport(null);
      setScanning(false);
      setSelectedScanRefs(new Set());
    }
  }, [open]);

  const runDryRun = async () => {
    const r = ref.trim().toUpperCase();
    if (!r) {
      toast.error("Enter a booking reference (e.g. SCWT-5BDD78)");
      return;
    }
    setChecking(true);
    setDryRun(null);
    try {
      const { data } = await api.post("/admin/bookings/stripe-verified-reconcile", {
        references: [r],
        dry_run: true,
      });
      setDryRun(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setChecking(false);
    }
  };

  const apply = async () => {
    if (!dryRun || dryRun.would_reconcile_count !== 1) return;
    const r = ref.trim().toUpperCase();
    setApplying(true);
    try {
      const { data } = await api.post("/admin/bookings/stripe-verified-reconcile", {
        references: [r],
        dry_run: false,
      });
      if (data.reconciled === 1) {
        toast.success(`${r} marked paid — Stripe verified.`);
        onApplied?.();
        onClose?.();
      } else {
        const err = data.errors?.[0]?.error || "Stripe declined to confirm paid at apply time.";
        toast.error(`No update applied: ${err}`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setApplying(false);
    }
  };

  const runScan = async () => {
    setScanning(true);
    setScanReport(null);
    setSelectedScanRefs(new Set());
    try {
      const { data } = await api.get(`/admin/payments/scan-stuck?days=${scanDays}&limit=500`);
      setScanReport(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setScanning(false);
    }
  };

  const downloadCsv = () => {
    const url = `${API_BASE}/admin/payments/scan-stuck.csv?days=${scanDays}&limit=500`;
    const token = localStorage.getItem("scwt_token");
    // Authed download — fetch then blob → object url so the Authorization
    // header is included.
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `stuck-payments-${scanDays}d.csv`;
        document.body.appendChild(a); a.click(); a.remove();
      })
      .catch((e) => toast.error(e.message));
  };

  const toggleScanRef = (r) => {
    setSelectedScanRefs((prev) => {
      const next = new Set(prev);
      if (next.has(r)) next.delete(r); else next.add(r);
      return next;
    });
  };

  const applySelectedFromScan = async () => {
    const refs = [...selectedScanRefs];
    if (refs.length === 0) return;
    setScanApplyBusy(true);
    try {
      // 1) Dry-run gate: re-check Stripe truth for each ref. Never
      //    apply on the stale scan report — Stripe state may have moved.
      const { data: dry } = await api.post("/admin/bookings/stripe-verified-reconcile", {
        references: refs, dry_run: true,
      });
      if (dry.would_reconcile_count === 0) {
        toast.error("Stripe no longer confirms paid for any selected — apply aborted.");
        setScanApplyBusy(false);
        return;
      }
      const confirmedRefs = dry.would_reconcile.map((r) => r.reference);
      const skipped = refs.filter((r) => !confirmedRefs.includes(r));
      // 2) Apply only the gate-confirmed refs.
      const { data: app } = await api.post("/admin/bookings/stripe-verified-reconcile", {
        references: confirmedRefs, dry_run: false,
      });
      const bits = [`${app.reconciled} reconciled`];
      if (skipped.length) bits.push(`${skipped.length} skipped (Stripe disagreed)`);
      toast.success(`Apply complete · ${bits.join(" · ")}`);
      onApplied?.();
      await runScan(); // refresh the report
      setSelectedScanRefs(new Set());
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setScanApplyBusy(false);
    }
  };

  const singleEntry = dryRun?.would_reconcile?.[0]
    || dryRun?.skipped_not_paid?.[0]
    || null;
  const singleSkippedAlreadyPaid = dryRun?.skipped_already_paid?.length > 0;

  const proposedColor = (p) => {
    if (p === "mark_paid") return "bg-emerald-100 text-emerald-800";
    if (p === "mark_abandoned") return "bg-amber-100 text-amber-800";
    if (p === "mark_failed") return "bg-rose-100 text-rose-800";
    return "bg-slate-100 text-slate-600";
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        data-testid="reconcile-stripe-dialog"
        className="max-w-3xl"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-600" />
            Reconcile from Stripe
          </DialogTitle>
          <DialogDescription>
            Verifies live Stripe payment state before touching any booking.
            Stripe is always the source of truth — this tool never marks
            a booking paid unless Stripe confirms it.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="single" className="mt-2">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="single" data-testid="reconcile-tab-single">Single booking</TabsTrigger>
            <TabsTrigger value="scan" data-testid="reconcile-tab-scan">Scan last {scanDays} days</TabsTrigger>
          </TabsList>

          {/* ─────────────── Single booking ─────────────── */}
          <TabsContent value="single" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="reconcile-ref">Booking reference</Label>
              <div className="flex gap-2">
                <Input
                  id="reconcile-ref"
                  data-testid="reconcile-ref-input"
                  value={ref}
                  onChange={(e) => { setRef(e.target.value); setDryRun(null); }}
                  placeholder="SCWT-5BDD78"
                  className="font-mono uppercase"
                />
                <Button
                  data-testid="reconcile-check-stripe"
                  onClick={runDryRun}
                  disabled={checking || !ref.trim()}
                  className="rounded-lg"
                >
                  {checking ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Checking…</> : <><RefreshCw className="w-4 h-4 mr-1.5" />Check Stripe</>}
                </Button>
              </div>
              <p className="text-[11px] text-slate-500">
                Dry-run first. Apply button only appears if Stripe confirms paid.
              </p>
            </div>

            {dryRun && !singleEntry && !singleSkippedAlreadyPaid && (
              <div data-testid="reconcile-no-candidates" className="p-4 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-700">
                No booking found for <span className="font-mono">{ref}</span> (or no Stripe identifier on file). Double-check the reference.
              </div>
            )}

            {dryRun && singleSkippedAlreadyPaid && (
              <div data-testid="reconcile-already-paid" className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-sm text-emerald-900">
                ✅ Booking <span className="font-mono">{ref}</span> is already marked paid. No action needed.
              </div>
            )}

            {singleEntry && (
              <div data-testid="reconcile-dry-result" className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                <div className="p-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
                  <div>
                    <div className="font-mono text-sm font-bold">{singleEntry.reference}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{singleEntry.customer_name} · {singleEntry.customer_email}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {singleEntry.found_via_deep_search && (
                      <span
                        data-testid="reconcile-deep-search-badge"
                        className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-800"
                        title="The on-file Stripe session was stale or missing — the paid object was located by searching metadata['reference']."
                      >
                        Found via deep search
                      </span>
                    )}
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${proposedColor(singleEntry.proposed_correction)}`}>
                      {singleEntry.proposed_correction || "no change"}
                    </span>
                  </div>
                </div>
                <div className="p-4 grid grid-cols-2 gap-3 text-xs">
                  <div><span className="text-slate-500">Package</span><div className="font-medium text-slate-900">{singleEntry.package || "—"}</div></div>
                  <div><span className="text-slate-500">Total</span><div className="font-medium text-slate-900">${(singleEntry.total ?? 0).toFixed?.(2) ?? singleEntry.total}</div></div>
                  <div><span className="text-slate-500">Current payment status</span><div className="font-medium text-slate-900">{singleEntry.current_payment_status}</div></div>
                  <div><span className="text-slate-500">Confidence</span><div className="font-medium text-slate-900">{singleEntry.confidence}</div></div>
                  <div className="col-span-2"><span className="text-slate-500">Stripe says</span><div className="font-mono text-[11px] text-slate-700 break-all">{singleEntry.stripe_status || "—"}</div></div>
                  <div className="col-span-2"><span className="text-slate-500">Session id</span><div className="font-mono text-[11px] text-slate-700 break-all">{singleEntry.session_id || "—"}</div></div>
                  <div className="col-span-2"><span className="text-slate-500">Payment intent</span><div className="font-mono text-[11px] text-slate-700 break-all">{singleEntry.payment_intent_id || "—"}</div></div>
                  <div className="col-span-2"><span className="text-slate-500">Reason</span><div className="text-slate-800">{singleEntry.reason}</div></div>
                </div>
                {singleEntry.proposed_correction === "mark_paid" && singleEntry.confidence === "high" ? (
                  <div className="p-4 border-t border-slate-100 bg-emerald-50 flex items-center justify-between">
                    <div className="text-sm text-emerald-900 inline-flex items-center gap-1.5">
                      <CheckCircle2 className="w-4 h-4" />
                      Stripe confirms paid. Safe to apply.
                    </div>
                    <Button
                      data-testid="reconcile-apply-single"
                      onClick={apply}
                      disabled={applying}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      {applying ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Applying…</> : "Apply · mark paid"}
                    </Button>
                  </div>
                ) : (
                  <div className="p-4 border-t border-slate-100 bg-amber-50 text-sm text-amber-900 inline-flex items-center gap-1.5">
                    <AlertTriangle className="w-4 h-4" />
                    Stripe does not confirm paid — apply button is intentionally hidden. No action taken.
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* ─────────────── 30-day scan ─────────────── */}
          <TabsContent value="scan" className="space-y-4 mt-4">
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Label htmlFor="scan-days">Days back</Label>
                <Input
                  id="scan-days" type="number" min={1} max={180}
                  data-testid="reconcile-scan-days"
                  value={scanDays} onChange={(e) => setScanDays(Math.max(1, Math.min(180, Number(e.target.value) || 30)))}
                  className="font-mono"
                />
              </div>
              <Button data-testid="reconcile-run-scan" onClick={runScan} disabled={scanning} className="rounded-lg">
                {scanning ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Scanning…</> : <><RefreshCw className="w-4 h-4 mr-1.5" />Run scan</>}
              </Button>
              {scanReport && (
                <Button variant="outline" onClick={downloadCsv} data-testid="reconcile-download-csv">
                  <Download className="w-4 h-4 mr-1.5" />CSV
                </Button>
              )}
            </div>

            {scanReport && (
              <>
                <div className="grid grid-cols-4 gap-2 text-xs">
                  <div className="p-3 rounded-lg bg-slate-100"><div className="text-slate-500">Total</div><div className="font-bold text-base">{scanReport.counts.total}</div></div>
                  <div className="p-3 rounded-lg bg-emerald-50"><div className="text-emerald-700">Mark paid</div><div className="font-bold text-base text-emerald-900">{scanReport.counts.high_confidence_mark_paid}</div></div>
                  <div className="p-3 rounded-lg bg-amber-50"><div className="text-amber-700">Mark abandoned</div><div className="font-bold text-base text-amber-900">{scanReport.counts.high_confidence_mark_abandoned}</div></div>
                  <div className="p-3 rounded-lg bg-slate-50"><div className="text-slate-500">No action</div><div className="font-bold text-base">{scanReport.counts.no_action_or_unverifiable}</div></div>
                </div>

                <div className="rounded-xl border border-slate-200 overflow-hidden max-h-[360px] overflow-y-auto">
                  <table className="w-full text-xs" data-testid="reconcile-scan-table">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left p-2 w-8"></th>
                        <th className="text-left p-2 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Ref</th>
                        <th className="text-left p-2 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Customer</th>
                        <th className="text-left p-2 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Package</th>
                        <th className="text-left p-2 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Current</th>
                        <th className="text-left p-2 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Stripe</th>
                        <th className="text-left p-2 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Proposed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scanReport.rows.map((r) => {
                        const applyable = r.proposed_correction === "mark_paid" && r.confidence === "high";
                        return (
                          <tr key={r.reference} className="border-t border-slate-100 hover:bg-slate-50/60">
                            <td className="p-2">
                              {applyable && (
                                <input
                                  type="checkbox"
                                  data-testid={`reconcile-scan-row-${r.reference}`}
                                  checked={selectedScanRefs.has(r.reference)}
                                  onChange={() => toggleScanRef(r.reference)}
                                />
                              )}
                            </td>
                            <td className="p-2 font-mono font-bold">{r.reference}</td>
                            <td className="p-2">{r.customer_name}<div className="text-slate-400 text-[10px]">{r.customer_email}</div></td>
                            <td className="p-2">{r.package}</td>
                            <td className="p-2">{r.current_payment_status}</td>
                            <td className="p-2 font-mono text-[10px] text-slate-600 max-w-[200px] truncate" title={r.stripe_status || ""}>{r.stripe_status || "—"}</td>
                            <td className="p-2"><span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${proposedColor(r.proposed_correction)}`}>{r.proposed_correction || "—"}</span></td>
                          </tr>
                        );
                      })}
                      {scanReport.rows.length === 0 && (
                        <tr><td colSpan={7} className="p-6 text-center text-slate-500">No stuck bookings in this window. ✨</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {selectedScanRefs.size > 0 && (
                  <div className="flex items-center justify-between p-3 rounded-xl bg-emerald-50 border border-emerald-200">
                    <div className="text-sm text-emerald-900 inline-flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      {selectedScanRefs.size} selected · only Stripe-confirmed rows can be checked
                    </div>
                    <Button
                      data-testid="reconcile-apply-selected"
                      onClick={applySelectedFromScan}
                      disabled={scanApplyBusy}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      {scanApplyBusy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Applying…</> : `Apply ${selectedScanRefs.size}`}
                    </Button>
                  </div>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-4">
          <div className="flex-1 text-[11px] text-slate-500 inline-flex items-center gap-1.5">
            <ShieldCheck className="w-3 h-3" />
            Stripe is the source of truth. This tool never refunds, captures, cancels, or modifies Stripe.
          </div>
          <Button variant="outline" onClick={onClose} data-testid="reconcile-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ReconcileFromStripeDialog;
